import { useHotkeys } from "@mantine/hooks";
import { Button } from "@win-reality/react-ui";
import { useDispatch, useSelector } from "react-redux";

import {
  incrementTaggingFlowStep,
  setTaggingFlowStep,
} from "../../../../store/slices/hotkeys";
import {
  putTag,
  removeTag,
  setTaggingState,
} from "../../../../store/slices/session";
import type { RootState } from "../../../../store/store";

/**
 * PlayResultStep - Select play result
 *
 * Options (3 columns):
 * - OUT (O), 1B (1), K (K)
 * - SAC (S), 2B (2), ꓘ (L)
 * - FC (F), 3B (3), BB (B)
 * - ROE (E), HR (H)
 */
export const PlayResultStep = () => {
  const tagData = useSelector((state: RootState) => state.session.tagData);
  const dispatch = useDispatch();
  const tagKey = "playResult";

  function handleTag(value: string) {
    dispatch(setTaggingState(true));
    if (tagData[tagKey] === value) {
      dispatch(removeTag({ key: tagKey }));
    } else {
      dispatch(putTag({ key: tagKey, value }));
      dispatch(incrementTaggingFlowStep());
    }
  }

  const hotkeys = {
    Out: "O",
    Sacrifice: "S",
    FieldersChoice: "F",
    Error: "E",
    Single: "1",
    Double: "2",
    Triple: "3",
    HomeRun: "H",
    StrikeoutSwinging: "K",
    StrikeoutLooking: "L",
    Walk: "B",
  };

  useHotkeys([
    [hotkeys.Out, () => handleTag("Out")],
    [hotkeys.Sacrifice, () => handleTag("Sacrifice")],
    [hotkeys.FieldersChoice, () => handleTag("FieldersChoice")],
    [hotkeys.Error, () => handleTag("Error")],
    [hotkeys.Single, () => handleTag("Single")],
    [hotkeys.Double, () => handleTag("Double")],
    [hotkeys.Triple, () => handleTag("Triple")],
    [hotkeys.HomeRun, () => handleTag("HomeRun")],
    [hotkeys.StrikeoutSwinging, () => handleTag("StrikeoutSwinging")],
    [hotkeys.StrikeoutLooking, () => handleTag("StrikeoutLooking")],
    [hotkeys.Walk, () => handleTag("Walk")],
  ]);

  return (
    <div className="flex w-full flex-col items-stretch gap-0">
      {/* Previously completed tags - clickable to jump back */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(0))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.prePitchStance ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(1))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.pitchType ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(2))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.pitchResult ?? "---"}
        </p>
      </div>

      {/* Instructions */}
      <div className="flex h-10 items-center justify-center">
        <p className="text-sm italic text-text-light">Select Play Result</p>
      </div>

      {/* Button Grid - 3 columns */}
      <div className="flex w-full items-start gap-0">
        {/* Left Column */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Out" ? "blue" : "ghost"}
              onClick={() => handleTag("Out")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                OUT
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Out}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Sacrifice" ? "blue" : "ghost"}
              onClick={() => handleTag("Sacrifice")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                SAC
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Sacrifice}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "FieldersChoice" ? "blue" : "ghost"}
              onClick={() => handleTag("FieldersChoice")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                FC
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.FieldersChoice}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Error" ? "blue" : "ghost"}
              onClick={() => handleTag("Error")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                ROE
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Error}
                </kbd>
              </span>
            </Button>
          </div>
        </div>

        {/* Middle Column */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Single" ? "blue" : "ghost"}
              onClick={() => handleTag("Single")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                1B
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Single}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Double" ? "blue" : "ghost"}
              onClick={() => handleTag("Double")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                2B
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Double}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Triple" ? "blue" : "ghost"}
              onClick={() => handleTag("Triple")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                3B
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Triple}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "HomeRun" ? "blue" : "ghost"}
              onClick={() => handleTag("HomeRun")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                HR
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.HomeRun}
                </kbd>
              </span>
            </Button>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-1 flex-col justify-start gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={
                tagData[tagKey] === "StrikeoutSwinging" ? "blue" : "ghost"
              }
              onClick={() => handleTag("StrikeoutSwinging")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                K
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.StrikeoutSwinging}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={
                tagData[tagKey] === "StrikeoutLooking" ? "blue" : "ghost"
              }
              onClick={() => handleTag("StrikeoutLooking")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                ꓘ
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.StrikeoutLooking}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Walk" ? "blue" : "ghost"}
              onClick={() => handleTag("Walk")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                BB
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Walk}
                </kbd>
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
